import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import DashboardLayout from '../components/DashboardLayout';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { COLUMN_MIN_SIZE, COLUMN_SIZE, DIALOG_CLOSE_BUTTON_STYLE, NEAR_FULLSCREEN_DIALOG_PAPER_SX } from '../constants/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const SubmittedPalletList = () => {
  
  // Dialog states
  const [payloadDialogOpen, setPayloadDialogOpen] = useState(false);
  const [netsuiteDialogOpen, setNetsuiteDialogOpen] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [selectedNetsuiteResponse, setSelectedNetsuiteResponse] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  
  // Nested dialog states
  const [nestedDialogOpen, setNestedDialogOpen] = useState(false);
  const [nestedDialogTitle, setNestedDialogTitle] = useState('');
  const [nestedDialogData, setNestedDialogData] = useState(null);
  const [nestedDialogLoading, setNestedDialogLoading] = useState(false);
  const [nestedColumnVisibility, setNestedColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('submitted-pallet-nested-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Column visibility states
  const [payloadColumnVisibility, setPayloadColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('submitted-pallet-payload-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [netsuiteColumnVisibility, setNetsuiteColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('submitted-pallet-netsuite-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // External API URL (separate API, not part of base URL)
  const apiUrl = 'https://jy-shipline-data-production.up.railway.app/submitted-palates-list';

  // Use React Query for caching external API data
  const { data: fetchedData, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['wms_data', 'submittedPalletList'],
    queryFn: async () => {
      try {
        console.log(`🔄 Fetching submitted pallet list from ${apiUrl}...`);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => response.statusText);
          throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}. ${errorText}`);
        }

        const result = await response.json();
        
        // Handle the response structure: { status: "success", count: 656, data: [...] }
        let data = [];
        if (result && result.status === 'success' && Array.isArray(result.data)) {
          data = result.data;
          console.log(`✅ Successfully loaded ${result.count || result.data.length} pallet records`);
        } else if (Array.isArray(result)) {
          data = result;
          console.log(`✅ Successfully loaded ${result.length} pallet records`);
        } else if (result && result.data && Array.isArray(result.data)) {
          data = result.data;
          console.log(`✅ Successfully loaded ${result.data.length} pallet records`);
        } else {
          throw new Error('Unexpected response format. Expected { status: "success", data: [...] }');
        }
        
        return data;
      } catch (err) {
        console.error('❌ Error fetching submitted pallet list:', err);
        throw err;
      }
    },
    staleTime: 0,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    retry: 1,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  // Extract data and error from query
  const data = fetchedData || [];
  const error = queryError ? (queryError.message || 'Failed to load submitted pallet list. Please check your connection and try again.') : null;

  // Refresh handler
  const fetchData = async () => {
    await refetch();
  };

  // Save column visibility states
  useEffect(() => {
    localStorage.setItem('submitted-pallet-payload-column-visibility', JSON.stringify(payloadColumnVisibility));
  }, [payloadColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('submitted-pallet-netsuite-column-visibility', JSON.stringify(netsuiteColumnVisibility));
  }, [netsuiteColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('submitted-pallet-nested-column-visibility', JSON.stringify(nestedColumnVisibility));
  }, [nestedColumnVisibility]);

  // Helper function to convert complex objects to table format (similar to BinTransfer)
  const convertToTableFormat = (data) => {
    if (!data || typeof data !== 'object') return [];
    
    // If data is an array, process each item
    if (Array.isArray(data)) {
      return data.map(item => convertToTableFormat(item)[0] || {});
    }
    
    // For single object, create a row with all properties
    const row = {};
    
    function processObject(obj, prefix = '') {
      Object.entries(obj).forEach(([key, value]) => {
        const newKey = prefix ? `${prefix}_${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Recursively flatten nested objects
          processObject(value, newKey);
        } else if (Array.isArray(value)) {
          // Store arrays as is for nested table display
          row[newKey] = value;
        } else {
          // Handle primitive values
          row[newKey] = value;
        }
      });
    }
    
    processObject(data);
    return [row];
  };

  // Helper function to format column header
  const formatColumnHeader = (key) => {
    if (!key || typeof key !== 'string') {
      return 'Unknown';
    }
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to create base column configuration
  const createBaseColumn = (key) => ({
    id: key || 'unknown',
    accessorKey: key || 'unknown',
    header: formatColumnHeader(key),
    minSize: COLUMN_MIN_SIZE,
    size: COLUMN_SIZE,
    enableSorting: true,
  });

  // Generate payload detail columns dynamically
  const payloadDetailColumns = useMemo(() => {
    if (!selectedPayload) return [];
    
    const dataArray = convertToTableFormat(selectedPayload);
    if (!dataArray.length) return [];
    
    const keys = Object.keys(dataArray[0]);
    
    return keys.map(key => {
      const baseColumn = createBaseColumn(key);
      
      return {
        ...baseColumn,
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return '-';
          }
          
          // Handle arrays - make them clickable for nested dialogs
          if (Array.isArray(value)) {
            if (value.length > 0) {
              return (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open dialog immediately
                    setNestedDialogOpen(true);
                    setNestedDialogLoading(true);
                    
                    // Set data after a brief delay to show loading spinner
                    setTimeout(() => {
                      setNestedDialogData(value);
                      setNestedDialogTitle(`Payload - ${formatColumnHeader(key)}`);
                      setNestedDialogLoading(false);
                    }, 100);
                  }}
                  sx={{
                    cursor: 'pointer',
                    color: 'primary.main',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  {value.length} items
                </Box>
              );
            }
            return '0 items';
          }
          
          // Handle objects
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          
          return String(value);
        },
      };
    });
  }, [selectedPayload]);

  // Generate netsuite response detail columns dynamically
  const netsuiteDetailColumns = useMemo(() => {
    if (!selectedNetsuiteResponse) return [];
    
    const dataArray = convertToTableFormat(selectedNetsuiteResponse);
    if (!dataArray.length) return [];
    
    const keys = Object.keys(dataArray[0]);
    
    return keys.map(key => {
      const baseColumn = createBaseColumn(key);
      
      return {
        ...baseColumn,
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return '-';
          }
          
          // Handle arrays - make them clickable for nested dialogs
          if (Array.isArray(value)) {
            if (value.length > 0) {
              return (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    // Open dialog immediately
                    setNestedDialogOpen(true);
                    setNestedDialogLoading(true);
                    
                    // Set data after a brief delay to show loading spinner
                    setTimeout(() => {
                      setNestedDialogData(value);
                      setNestedDialogTitle(`NetSuite Response - ${formatColumnHeader(key)}`);
                      setNestedDialogLoading(false);
                    }, 100);
                  }}
                  sx={{
                    cursor: 'pointer',
                    color: 'primary.main',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: 'primary.dark',
                    },
                  }}
                >
                  {value.length} items
                </Box>
              );
            }
            return '0 items';
          }
          
          // Handle objects
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          
          return String(value);
        },
      };
    });
  }, [selectedNetsuiteResponse]);

  // Generate nested dialog columns dynamically
  const nestedDialogColumns = useMemo(() => {
    if (!nestedDialogData || !nestedDialogData.length) return [];
    
    const keys = Object.keys(nestedDialogData[0]);
    
    return keys.map(key => {
      const baseColumn = createBaseColumn(key);
      
      return {
        ...baseColumn,
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined) {
            return '-';
          }
          if (typeof value === 'object') {
            if (Array.isArray(value)) {
              return value.length > 0 ? `Array (${value.length} items)` : 'Empty Array';
            }
            return JSON.stringify(value);
          }
          return String(value);
        }
      };
    });
  }, [nestedDialogData]);

  // Handle payload cell click
  const handlePayloadClick = (row) => {
    setSelectedPayload(row.payload);
    setSelectedRowId(row.id || row.pallet_number);
    setPayloadDialogOpen(true);
  };

  // Handle netsuite response cell click
  const handleNetsuiteClick = (row) => {
    setSelectedNetsuiteResponse(row.netsuite_response);
    setSelectedRowId(row.id || row.pallet_number);
    setNetsuiteDialogOpen(true);
  };

  // Convert payload to table data format
  const payloadTableData = useMemo(() => {
    if (!selectedPayload) return [];
    return convertToTableFormat(selectedPayload);
  }, [selectedPayload]);

  // Convert netsuite response to table data format
  const netsuiteTableData = useMemo(() => {
    if (!selectedNetsuiteResponse) return [];
    return convertToTableFormat(selectedNetsuiteResponse);
  }, [selectedNetsuiteResponse]);

  // Define columns for the main table
  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'id',
        header: 'ID',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return value || '-';
        }
      },
      {
        accessorKey: 'internal_id',
        header: 'Internal ID',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return value || '-';
        }
      },
      {
        accessorKey: 'pallet_number',
        header: 'Pallet Number',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return value || '-';
        }
      },
      {
        accessorKey: 'pallet_size',
        header: 'Pallet Size',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          return value || '-';
        }
      },
      {
        accessorKey: 'payload',
        header: 'Payload',
        minSize: 80,
        size: 100,
        maxSize: 120,
        cell: info => {
          const value = info.getValue();
          const row = info.row.original;
          if (!value || typeof value !== 'object') return '-';
          const hasData = Array.isArray(value) ? value.length > 0 : (value && typeof value === 'object' && Object.keys(value).length > 0);
          return (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handlePayloadClick(row);
              }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  opacity: 0.8,
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography variant="body2">{hasData ? 'View Details' : 'No Data'}</Typography>
            </Box>
          );
        }
      },
      {
        accessorKey: 'netsuite_response',
        header: 'NetSuite Response',
        minSize: 80,
        size: 100,
        maxSize: 120,
        cell: info => {
          const value = info.getValue();
          const row = info.row.original;
          if (!value || typeof value !== 'object') return '-';
          const hasData = Array.isArray(value) ? value.length > 0 : (value && typeof value === 'object' && Object.keys(value).length > 0);
          // Determine NetSuite success
          let success;
          try {
            const netsuiteResponse = value?.netsuite_acknowledgment || value;
            success = netsuiteResponse?.success;
          } catch {}
          return (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleNetsuiteClick(row);
              }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  opacity: 0.8,
                  textDecoration: 'underline',
                },
              }}
            >
              {success === true && (
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4caf50' }} />
              )}
              {success === false && (
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#e53935' }} />
              )}
              <Typography variant="body2">{hasData ? 'View Details' : 'No Data'}</Typography>
            </Box>
          );
        }
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
      },
    ];
  }, []);

  const submittedPalletMainGrid = useDataGridProTable({
    data,
    loading: isLoading,
    error,
    title: 'Submitted Pallet List',
    columns,
    onDataRefresh: fetchData,
  });

  const submittedPalletPayloadGrid = useDataGridProTable({
    data: payloadTableData || [],
    columns: payloadDetailColumns,
    loading: false,
    title: 'sp-payload',
    initialColumnVisibility: payloadColumnVisibility,
    onColumnVisibilityChange: setPayloadColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      if (!row) return;
      try {
        const candidateEntries = Object.entries(row).filter(([k, v]) =>
          Array.isArray(v) && v.length > 0
        );
        if (candidateEntries.length > 0) {
          const [key, value] = candidateEntries[0];
          setNestedDialogOpen(true);
          setNestedDialogLoading(true);
          setTimeout(() => {
            setNestedDialogData(value);
            setNestedDialogTitle(`Payload - ${formatColumnHeader(key)}`);
            setNestedDialogLoading(false);
          }, 100);
        }
      } catch {}
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
  });

  const submittedPalletNetsuiteGrid = useDataGridProTable({
    data: netsuiteTableData || [],
    columns: netsuiteDetailColumns,
    loading: false,
    title: 'sp-netsuite',
    initialColumnVisibility: netsuiteColumnVisibility,
    onColumnVisibilityChange: setNetsuiteColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      if (!row) return;
      try {
        const candidateEntries = Object.entries(row).filter(([k, v]) =>
          Array.isArray(v) && v.length > 0
        );
        if (candidateEntries.length > 0) {
          const [key, value] = candidateEntries[0];
          setNestedDialogOpen(true);
          setNestedDialogLoading(true);
          setTimeout(() => {
            setNestedDialogData(value);
            setNestedDialogTitle(`NetSuite Response - ${formatColumnHeader(key)}`);
            setNestedDialogLoading(false);
          }, 100);
        }
      } catch {}
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
  });

  const submittedPalletNestedGrid = useDataGridProTable({
    data: nestedDialogData || [],
    columns: nestedDialogColumns,
    loading: false,
    title: 'sp-nested',
    initialColumnVisibility: nestedColumnVisibility,
    onColumnVisibilityChange: setNestedColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
  });

  return (
    <DashboardLayout>
      <MuiXDataGridSurface hook={submittedPalletMainGrid} DataGridPro={DataGridPro} />

      {/* Payload Details Dialog */}
      <Dialog
        open={payloadDialogOpen}
        onClose={() => setPayloadDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Payload Details {selectedRowId && `- ${selectedRowId}`}</Typography>
            <IconButton
              onClick={() => setPayloadDialogOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_STYLE}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {payloadTableData && payloadTableData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={submittedPalletPayloadGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography>No payload data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* NetSuite Response Details Dialog */}
      <Dialog
        open={netsuiteDialogOpen}
        onClose={() => setNetsuiteDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">NetSuite Response Details {selectedRowId && `- ${selectedRowId}`}</Typography>
            <IconButton
              onClick={() => setNetsuiteDialogOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_STYLE}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {netsuiteTableData && netsuiteTableData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={submittedPalletNetsuiteGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography>No NetSuite response data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Nested Data Dialog */}
      <Dialog
        open={nestedDialogOpen}
        onClose={() => setNestedDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{nestedDialogTitle}</Typography>
            <IconButton
              onClick={() => setNestedDialogOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_STYLE}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {nestedDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : nestedDialogData && nestedDialogData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={submittedPalletNestedGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography>No nested data available.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SubmittedPalletList;
